import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, User as UserIcon, Bell, CheckCheck, Inbox, Mail, Download, KeyRound, Search, Layers, Loader2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { usePWA } from "@/context/PWAContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import type { NotificationItem } from "@/types/notification";
import { CommandSearch } from "./CommandSearch";

const titles: Record<string, string> = {
  "/dashboard": "Overview & Performance",
  "/tasks": "My Day & Follow-Up Tasks",
  "/deals": "Sales Pipeline",
  "/leads": "Leads Directory",
  "/samples": "Samples",
  "/orders": "Orders & Dispatch",
  "/complaints": "Complaints",
  "/inventory": "Inventory & Stock",
  "/inventory/receipts": "Goods Receipts",
  "/inventory/warehouses": "Warehouses",
  "/suppliers": "Suppliers & Job Workers",
  "/production": "Production Board",
  "/settings/processes": "Production Processes",
  "/agents": "Agents & Brokers",
  "/audit-log": "Audit Log",
  "/users": "User Directory & Access",
  "/roles": "Role & Permission Management",
  "/settings/company": "Company Profile & Legal Details",
  "/settings/currencies": "Multi-Currency Management",
  "/settings/countries": "Country & Regional Settings",
};

interface TopbarProps {
  collapsed?: boolean;
  onToggleSidebar?: () => void;
}

export function Topbar({ collapsed = false, onToggleSidebar }: TopbarProps = {}) {
  const { user, logout, hasPermission } = useAuth();
  const { success } = useToast();
  const { canInstall, installApp } = usePWA();
  const navigate = useNavigate();
  const location = useLocation();

  const getTitle = () => {
    if (location.pathname === "/leads/new") return "Create New Lead";
    if (location.pathname.startsWith("/leads/") && location.pathname.endsWith("/edit")) return "Edit Lead Dossier";
    if (location.pathname.startsWith("/deals/") && location.pathname !== "/deals") return "Deal Workspace";
    if (location.pathname.startsWith("/production/")) return "Work Order";
    return titles[location.pathname] || "Textile CRM";
  };

  const currentTitle = getTitle();
  const searchShortcutLabel = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
    ? "⌘ K"
    : "Ctrl K";

  // Notification state
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [notifMenuOpen, setNotifMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onShortcut);
    return () => window.removeEventListener("keydown", onShortcut);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get("/api/Notification/UnreadCount");
      const data = unwrapObject<{ count: number }>(res);
      setUnreadCount(data.count || 0);
    } catch {
      // ignore
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get("/api/Notification/List?limit=15");
      const list = unwrapArray<NotificationItem>(res);
      setNotifications(list);
    } catch {
      // ignore
    }
  };

  // Poll unread count every 60 seconds and on route change
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  // When opening dropdown, fetch latest notifications
  useEffect(() => {
    if (notifMenuOpen) {
      fetchNotifications();
    }
  }, [notifMenuOpen]);

  const handleNotificationClick = async (notif: NotificationItem) => {
    if (!notif.read_at) {
      try {
        await api.patch(`/api/Notification/MarkRead/${notif.id}`);
        setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, read_at: new Date().toISOString() } : n))
        );
      } catch {
        // ignore
      }
    }
    if (notif.link) {
      setNotifMenuOpen(false);
      navigate(notif.link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch("/api/Notification/MarkAllRead");
      setUnreadCount(0);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: new Date().toISOString() }))
      );
    } catch {
      // ignore
    }
  };

  const handleLogout = async () => {
    await logout();
    success("Logged Out", "You have safely signed out of your session.");
    navigate("/login");
  };

  const initials = user?.FirstName
    ? `${user.FirstName[0]}${user.LastName ? user.LastName[0] : ""}`.toUpperCase()
    : "U";

  const formatRelativeTime = (dateStr: string) => {
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay === 1) return "Yesterday";
    return new Date(dateStr).toLocaleDateString([], { month: "short", day: "numeric" });
  };

  return (
    <header className="h-11 bg-[#363636] text-white pl-0 pr-3 md:pr-4 flex items-center justify-between sticky top-0 z-40 select-none w-full">
      {/* Left: Brand Identity (matches sidebar width exactly) + Search (near the curve) */}
      <div className="flex items-center min-w-0">
        {/* Brand Logo & Name: expands/collapses in sync with Sidebar */}
        <div 
          onClick={() => navigate("/dashboard")}
          className={cn(
            "flex items-center cursor-pointer group shrink-0 transition-[width] duration-300 ease-in-out overflow-hidden",
            collapsed ? "w-16 justify-center" : "w-56 justify-center gap-2.5 px-2"
          )}
        >
          <div className="size-8 rounded-lg bg-[#e06d20] flex items-center justify-center text-white font-bold shadow-xs shrink-0 group-hover:bg-[#c25914] transition-colors">
            <Layers className="size-[18px]" />
          </div>
          {!collapsed && (
            <div className="flex flex-col items-center justify-center whitespace-nowrap overflow-hidden text-center">
              <span className="font-extrabold text-[13px] tracking-wider text-white leading-tight">
                NELGATES
              </span>
              <span className="text-[9px] tracking-widest uppercase text-slate-300 font-bold leading-none mt-0.5">
                Textile CRM
              </span>
            </div>
          )}
        </div>

      </div>

      {/* Centre: the search pill, which opens the search panel */}
      <div className="flex min-w-0 flex-1 justify-end px-2 sm:justify-center sm:px-6">
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="Open search"
          className="group hidden h-8 w-full max-w-lg items-center gap-2.5 rounded-full border border-white/15 bg-white/10 pl-3.5 pr-1.5 text-left text-[13px] text-slate-300 outline-none transition-all duration-200 hover:border-white/30 hover:bg-white/15 hover:text-white hover:shadow-[0_0_0_4px_rgba(224,109,32,0.18)] focus-visible:border-[#e06d20] focus-visible:shadow-[0_0_0_4px_rgba(224,109,32,0.3)] sm:flex"
        >
          <Search className="size-4 shrink-0 text-slate-300 transition-colors group-hover:text-white" />
          <span className="flex-1 truncate">Search buyers, orders, products, rolls…</span>
          <kbd className="rounded-full border border-white/20 bg-black/20 px-2 py-0.5 text-[10px] font-medium tracking-wide text-slate-300">{searchShortcutLabel}</kbd>
        </button>

        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          title="Search"
          aria-label="Open search"
          className="size-7.5 shrink-0 rounded-md text-white hover:bg-white/10 sm:hidden"
          onClick={() => setSearchOpen(true)}
        >
          <Search className="size-4" />
        </Button>
        <CommandSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      </div>

      {/* Right: Actions, Notifications & Profile */}
      <div className="flex items-center gap-1.5">
        {/* Notification Bell Dropdown */}
        <DropdownMenu open={notifMenuOpen} onOpenChange={setNotifMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              title="Notifications"
              className="relative text-white hover:bg-white/10 size-7.5 rounded-md"
            >
              <Bell className="size-3.5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center shadow-xs">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 max-h-[450px] overflow-y-auto p-0">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Notifications {unreadCount > 0 && `(${unreadCount})`}
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="text-xs text-primary hover:underline font-semibold flex items-center gap-1"
                >
                  <CheckCheck className="size-3.5" />
                  <span>Mark all read</span>
                </button>
              )}
            </div>

            <div className="divide-y divide-border">
              {notifications.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Inbox className="size-8 mx-auto mb-1.5 opacity-40" />
                  <p className="text-xs">No notifications yet</p>
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`p-3 text-left transition-colors cursor-pointer hover:bg-muted/40 flex items-start gap-2.5 ${
                      !n.read_at ? "bg-primary/5" : ""
                    }`}
                  >
                    {!n.read_at && (
                      <span className="size-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p
                          className={`text-xs truncate ${
                            !n.read_at ? "font-bold text-foreground" : "font-medium text-foreground/80"
                          }`}
                        >
                          {n.title}
                        </p>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {formatRelativeTime(n.created_on)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {n.body}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* PWA Install Button */}
        {canInstall && (
          <Button
            variant="ghost"
            size="sm"
            onClick={installApp}
            className="h-7 px-2 gap-1 text-[11px] text-white hover:bg-white/10 hidden sm:flex cursor-pointer"
            title="Install Nelgates CRM as desktop or mobile app"
          >
            <Download className="size-3" />
            <span>Install</span>
          </Button>
        )}

        {/* User Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 pl-1 pr-1.5 py-0.5 h-8 rounded-lg hover:bg-white/10 text-white"
            >
              <Avatar className="size-6.5 border border-white/20 bg-[#e06d20] text-white">
                <AvatarFallback className="bg-[#e06d20] text-white text-[11px] font-bold">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col text-left hidden sm:flex">
                <span className="text-xs font-semibold leading-tight text-white">
                  {user?.FirstName} {user?.LastName || ""}
                </span>
                <span className="text-[9px] text-slate-300 leading-tight">
                  {user?.UserRole?.RoleName || "Super Admin"}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-semibold">{user?.FirstName} {user?.LastName || ""}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.Email}</p>
                <div className="pt-1">
                  <Badge variant="blue" className="text-[10px] font-normal">
                    {user?.UserRole?.RoleName || "Administrator"}
                  </Badge>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer">
              <UserIcon className="size-4 mr-2" />
              <span>Dashboard</span>
            </DropdownMenuItem>
            {(hasPermission("email.read") || hasPermission("email.send")) && (
              <DropdownMenuItem onClick={() => navigate("/emails?tab=settings")} className="cursor-pointer">
                <Mail className="size-4 mr-2" />
                <span>Email Connection</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate("/change-password")} className="cursor-pointer">
              <KeyRound className="size-4 mr-2" />
              <span>Change Password</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
            >
              <LogOut className="size-4 mr-2" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
